import React, { useState, useEffect, useCallback, useRef } from 'react';
import { approveRequestAPI, geocoderAPI, showApiError } from '../../services/api';
import './RegistrationComponents.css';

const EquipmentRequestRegistration = () => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [equipmentRequests, setEquipmentRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const isInitialLoad = useRef(true);
  const [coordinates, setCoordinates] = useState({ latitude: null, longitude: null });
  const [searchingCoordinates, setSearchingCoordinates] = useState(false);
  const [editedAddress, setEditedAddress] = useState('');

  // 장비 설정 승인 요청 목록 로드
  const loadApproveRequests = useCallback(async (reset = false) => {
    if (loading) return;
    
    console.log('loadApproveRequests called - reset:', reset, 'loading:', loading);
    setLoading(true);
    try {
      const condition = {
        keyword: searchKeyword,
        cursor: reset ? null : cursor,
        limit: 20
      };
      
      console.log('API call condition:', condition);
      const response = await approveRequestAPI.getAll(condition);
      
      console.log('EquipmentRequestRegistration - API response:', response);
      console.log('EquipmentRequestRegistration - Approve requests data:', response.data);
      
      if (reset) {
        console.log('Resetting approve requests with:', response.data);
        setEquipmentRequests(response.data || []);
      } else {
        console.log('Adding to existing approve requests:', response.data);
        setEquipmentRequests(prev => {
          const newData = [...prev, ...(response.data || [])];
          console.log('New approve requests state:', newData);
          return newData;
        });
      }
      
      setHasMore(response.hasNext || false);
      setCursor(response.nextCursor || null);
    } catch (error) {
      console.error('장비 설정 승인 요청 목록 로드 실패:', error);
      alert('장비 설정 승인 요청 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword, cursor]);

  // 초기 데이터 로드 (한 번만 실행)
  useEffect(() => {
    if (isInitialLoad.current) {
      console.log('useEffect triggered - initial load');
      loadApproveRequests();
      isInitialLoad.current = false;
    }
  }, [loadApproveRequests]);

  // 검색
  const handleSearch = () => {
    setCursor(null);
    setHasMore(true);
    loadApproveRequests(true);
  };

  // 엔터키 검색 핸들러
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 더 많은 데이터 로드 (무한스크롤)
  const loadMore = () => {
    if (hasMore && !loading) {
      loadApproveRequests();
    }
  };

  const handleViewDetails = async (request) => {
    try {
      // 상세 정보를 API에서 가져오기
      const detailedRequest = await approveRequestAPI.get(request.id);
      setSelectedRequest(detailedRequest);
      setCoordinates({ latitude: null, longitude: null }); // 좌표 초기화
      setEditedAddress(detailedRequest.powerLocation?.address || ''); // 주소 초기화
      setShowModal(true);
    } catch (error) {
      console.error('장비 설정 상세 정보 로드 실패:', error);
      showApiError(error);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
    setCoordinates({ latitude: null, longitude: null });
    setEditedAddress('');
  };

  // 좌표 검색 기능
  const handleSearchCoordinates = async () => {
    const addressToSearch = editedAddress.trim();
    if (!addressToSearch) {
      alert('주소를 먼저 입력해주세요.');
      return;
    }

    setSearchingCoordinates(true);
    try {
      const result = await geocoderAPI.getCoordinates(addressToSearch);
      
      if (result && result.latitude && result.longitude) {
        setCoordinates({
          latitude: result.latitude,
          longitude: result.longitude
        });
        alert(`좌표를 찾았습니다!\n위도: ${result.latitude}\n경도: ${result.longitude}`);
      } else {
        alert('해당 주소의 좌표를 찾을 수 없습니다. 주소를 확인해주세요.');
      }
    } catch (error) {
      console.error('좌표 검색 실패:', error);
      showApiError(error);
    } finally {
      setSearchingCoordinates(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('정말로 이 장비 설정 요청을 삭제하시겠습니까?')) {
      try {
        await approveRequestAPI.delete(id);
        alert('장비 설정 요청이 성공적으로 삭제되었습니다!');
        setCursor(null);
        setHasMore(true);
        loadApproveRequests(true); // 목록 새로고침
      } catch (error) {
        console.error('장비 설정 요청 삭제 실패:', error);
        alert('장비 설정 요청 삭제에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  const handleApprove = async (id) => {
    // 좌표가 없으면 경고
    if (!coordinates.latitude || !coordinates.longitude) {
      alert('승인하기 전에 좌표를 검색해주세요.');
      return;
    }

    try {
      console.log('승인 요청 시작 - ID:', id, '좌표:', coordinates);
      const response = await approveRequestAPI.update(id, 'APPROVED', coordinates.latitude, coordinates.longitude);
      console.log('승인 응답:', response);
      alert('장비 설정이 승인되었습니다!');
      setShowModal(false);
      setSelectedRequest(null);
      setCoordinates({ latitude: null, longitude: null });
      setCursor(null);
      setHasMore(true);
      loadApproveRequests(true); // 목록 새로고침
    } catch (error) {
      console.error('장비 설정 승인 실패:', error);
      console.error('에러 상세:', error.response?.data);
      console.error('에러 상태:', error.response?.status);
      showApiError(error);
    }
  };


  return (
    <div className="equipment-approval-component">
      <div className="equipment-approval-header">
        <h2>⚙️ 장비 설정 승인</h2>
        <p>외부 RTU에서 전송된 장비 설정 목록을 확인하고 승인합니다.</p>
      </div>

      <div className="equipment-approval-layout">
        <div className="equipment-approval-list-section">
          <div className="equipment-approval-list-header">
            <h3>장비 설정 승인 목록</h3>
            <div className="equipment-approval-search-section">
              <input
                type="text"
                placeholder="시리얼 코드 또는 발전소명으로 검색"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="equipment-approval-search-input"
              />
              <button onClick={handleSearch} className="btn btn-primary btn-sm">
                검색
              </button>
            </div>
          </div>
          <div className="equipment-approval-table">
            <div className="equipment-approval-table-header">
              <div className="equipment-approval-table-cell">시리얼 코드</div>
              <div className="equipment-approval-table-cell">발전소 명</div>
              <div className="equipment-approval-table-cell">승인 상태</div>
              <div className="equipment-approval-table-cell">요청 시간</div>
              <div className="equipment-approval-table-cell">관리</div>
            </div>
            {equipmentRequests.map(request => (
              <div key={request.id} className="equipment-approval-table-row">
                <div className="equipment-approval-table-cell">{request.powerLocation?.serialCode || request.serialCode || '-'}</div>
                <div className="equipment-approval-table-cell">{request.powerLocation?.name || request.locationName || '-'}</div>
                <div className="equipment-approval-table-cell">
                  <span className={`equipment-approval-status-badge ${
                    request.status === 'APPROVED' ? 'active' : 
                    request.status === 'AWAITING' ? 'pending' : 'inactive'
                  }`}>
                    {request.status === 'APPROVED' ? '승인완료' : 
                     request.status === 'AWAITING' ? '대기중' : '거부됨'}
                  </span>
                </div>
                <div className="equipment-approval-table-cell">
                  {request.createdAt ? new Date(request.createdAt).toLocaleString('ko-KR') : '-'}
                </div>
                <div className="equipment-approval-table-cell">
                  <div className="equipment-approval-action-buttons">
                    <button 
                      className="btn btn-info btn-sm"
                      onClick={() => handleViewDetails(request)}
                    >
                      상세보기
                    </button>
                    <button 
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(request.id)}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {/* 무한스크롤 로딩 */}
            {loading && (
              <div className="equipment-approval-loading-row">
                <div className="equipment-approval-loading-spinner"></div>
                <span>로딩 중...</span>
              </div>
            )}
            
            {/* 더 보기 버튼 */}
            {hasMore && !loading && (
              <div className="equipment-approval-load-more-section">
                <button onClick={loadMore} className="btn btn-secondary">
                  더 보기
                </button>
              </div>
            )}
            
            {/* 더 이상 데이터가 없을 때 */}
            {!hasMore && equipmentRequests.length > 0 && (
              <div className="equipment-approval-no-more-data">
                <span>모든 데이터를 불러왔습니다.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 장비 설정 상세 모달 */}
      {showModal && selectedRequest && (
        <div className="equipment-approval-modal-overlay">
          <div className="equipment-approval-modal-content">
            <div className="equipment-approval-modal-header">
              <h3>장비 설정 상세 정보</h3>
              <button className="equipment-approval-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="equipment-approval-modal-body">
              <div className="equipment-approval-info">
                 {/* 기본 정보 그룹 */}
                 <div className="equipment-approval-info-section">
                   <h4>기본 정보</h4>
                   <div className="equipment-approval-info-grid">
                     <div className="equipment-approval-info-item">
                       <label>시리얼 코드:</label>
                       <span>{selectedRequest.powerLocation?.serialCode || '-'}</span>
                     </div>
                     <div className="equipment-approval-info-item">
                       <label>발전소 명:</label>
                       <span>{selectedRequest.powerLocation?.name || '-'}</span>
                     </div>
                     <div className="equipment-approval-info-item">
                       <label>설비 용량:</label>
                       <span>{selectedRequest.powerLocation?.capacity ? `${selectedRequest.powerLocation.capacity}kW` : '-'}</span>
                     </div>
                   </div>
                 </div>

                 {/* 주소 및 좌표 정보 그룹 */}
                 <div className="equipment-approval-info-section">
                   <h4>주소 및 좌표 정보</h4>
                   <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                     <div className="equipment-approval-info-item" style={{ width: '100%' }}>
                       <label>주소:</label>
                       <input
                         type="text"
                         value={editedAddress}
                         onChange={(e) => setEditedAddress(e.target.value)}
                         placeholder="주소를 입력하세요"
                         style={{
                           width: '100%',
                           padding: '0.5rem',
                           border: '1px solid #ced4da',
                           borderRadius: '4px',
                           fontSize: '0.9rem'
                         }}
                       />
                     </div>
                     
                     {/* 좌표 검색 섹션 (승인 대기 중일 때만 표시) */}
                     {selectedRequest.status === 'AWAITING' && (
                       <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                         <div style={{ marginBottom: '0.75rem' }}>
                           <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#495057', fontSize: '0.9rem' }}>
                             좌표 검색
                           </label>
                           <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                             <button
                               type="button"
                               className="btn btn-info btn-sm"
                               onClick={handleSearchCoordinates}
                               disabled={searchingCoordinates || !editedAddress.trim()}
                               style={{ minWidth: '120px' }}
                             >
                               {searchingCoordinates ? '검색 중...' : '좌표 검색'}
                             </button>
                             {coordinates.latitude && coordinates.longitude && (
                               <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                 <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                   <span style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: '600' }}>위도:</span>
                                   <span style={{ fontFamily: 'monospace', fontWeight: '600', color: '#495057' }}>{coordinates.latitude}</span>
                                 </div>
                                 <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                   <span style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: '600' }}>경도:</span>
                                   <span style={{ fontFamily: 'monospace', fontWeight: '600', color: '#495057' }}>{coordinates.longitude}</span>
                                 </div>
                               </div>
                             )}
                           </div>
                           {!coordinates.latitude && !coordinates.longitude && (
                             <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#dc3545' }}>
                               ⚠️ 승인하기 전에 좌표를 검색해주세요.
                             </p>
                           )}
                         </div>
                       </div>
                     )}
                   </div>
                 </div>

                 {/* 요청 정보 그룹 */}
                 <div className="equipment-approval-info-section">
                   <h4>요청 정보</h4>
                   <div className="equipment-approval-info-grid">
                     <div className="equipment-approval-info-item">
                       <label>요청 시간:</label>
                       <span>{selectedRequest.createdAt ? new Date(selectedRequest.createdAt).toLocaleString('ko-KR') : '-'}</span>
                     </div>
                     <div className="equipment-approval-info-item">
                       <label>승인 상태:</label>
                       <span className={`equipment-approval-status-badge ${
                         selectedRequest.status === 'APPROVED' ? 'active' : 
                         selectedRequest.status === 'AWAITING' ? 'pending' : 'inactive'
                       }`}>
                         {selectedRequest.status === 'APPROVED' ? '승인완료' : 
                          selectedRequest.status === 'AWAITING' ? '대기중' : '거부됨'}
                       </span>
                     </div>
                   </div>
                 </div>

                {selectedRequest.inverterConfigRequests && selectedRequest.inverterConfigRequests.length > 0 && (
                  <div className="equipment-approval-info-section">
                    <h4>인버터 목록</h4>
                    <div className="equipment-approval-equipment-list">
                      {selectedRequest.inverterConfigRequests.map((inverter, index) => (
                        <div key={index} className="equipment-approval-equipment-item">
                          <div className="equipment-approval-equipment-header">
                            <strong>{inverter.name}</strong>
                            <span className="equipment-approval-equipment-type">인버터</span>
                          </div>
                          <div className="equipment-approval-equipment-details">
                            <div className="equipment-approval-detail-row">
                              <span>RTU 인덱스:</span>
                              <span>{inverter.rtuIdx ?? '-'}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>프로토콜:</span>
                              <span>{inverter.inverterType || '-'}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>ID:</span>
                              <span>{inverter.inverterId}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>포트:</span>
                              <span>{inverter.port}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>보드레이트:</span>
                              <span>{inverter.baudrate}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>바이트 크기:</span>
                              <span>{inverter.bytesize}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>타임아웃:</span>
                              <span>{inverter.timeout}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>용량:</span>
                              <span>{inverter.capacity}kW</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedRequest.temperatureConfigRequests && selectedRequest.temperatureConfigRequests.length > 0 && (
                  <div className="equipment-approval-info-section">
                    <h4>온도 센서 목록</h4>
                    <div className="equipment-approval-equipment-list">
                      {selectedRequest.temperatureConfigRequests.map((sensor, index) => (
                        <div key={index} className="equipment-approval-equipment-item">
                          <div className="equipment-approval-equipment-header">
                            <strong>{sensor.name}</strong>
                            <span className="equipment-approval-equipment-type">온도센서</span>
                          </div>
                          <div className="equipment-approval-equipment-details">
                            <div className="equipment-approval-detail-row">
                              <span>RTU 인덱스:</span>
                              <span>{sensor.rtuIdx ?? '-'}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>센서 ID:</span>
                              <span>{sensor.sensorId}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>프로토콜:</span>
                              <span>{sensor.protocolType}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>포트:</span>
                              <span>{sensor.port}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>보드레이트:</span>
                              <span>{sensor.baudrate}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>바이트 크기:</span>
                              <span>{sensor.bytesize}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>타임아웃:</span>
                              <span>{sensor.timeout}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedRequest.panelConfigRequests && selectedRequest.panelConfigRequests.length > 0 && (
                  <div className="equipment-approval-info-section">
                    <h4>판넬 목록</h4>
                    <div className="equipment-approval-equipment-list">
                      {selectedRequest.panelConfigRequests.map((panel, index) => (
                        <div key={index} className="equipment-approval-equipment-item">
                          <div className="equipment-approval-equipment-header">
                            <strong>{panel.name}</strong>
                            <span className="equipment-approval-equipment-type">판넬</span>
                          </div>
                          <div className="equipment-approval-equipment-details">
                            <div className="equipment-approval-detail-row">
                              <span>RTU 인덱스:</span>
                              <span>{panel.rtuIdx ?? '-'}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>판넬 ID:</span>
                              <span>{panel.panelId}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>채널:</span>
                              <span>{panel.channel}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>포트:</span>
                              <span>{panel.port}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>보드레이트:</span>
                              <span>{panel.baudrate}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>바이트 크기:</span>
                              <span>{panel.bytesize}</span>
                            </div>
                            <div className="equipment-approval-detail-row">
                              <span>타임아웃:</span>
                              <span>{panel.timeout}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="equipment-approval-modal-footer">
              <div className="equipment-approval-modal-footer-buttons">
                {selectedRequest.status === 'AWAITING' && (
                  <button 
                    className="btn btn-success"
                    onClick={() => handleApprove(selectedRequest.id)}
                  >
                    승인
                  </button>
                )}
                <button 
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentRequestRegistration;
